import axios, { type InternalAxiosRequestConfig } from 'axios';
import { sysApiBaseUrl } from '../config/env';
import { clearSysTokens, getSysRefreshToken, getSysToken, setSysTokens } from './sysAuthStorage';
import { refreshAccessToken } from './sysAuthRefresh';
import { isSysApiSuccess, SYS_CODE_RELOGIN } from './sysApiCodes';
import type { LoginRes, ResultLoginRes } from './types/sysAuth';

const baseURL = sysApiBaseUrl;

export const sysHttp = axios.create({
  baseURL,
  timeout: 15000
});



let refreshChain: Promise<void> | null = null;

function loginPageHref(): string {
  const base = import.meta.env.BASE_URL || '/';
  const withSlash = base.endsWith('/') ? base : `${base}/`;
  return `${withSlash}admin/login`;
}

function forceReloginRedirect() {
  clearSysTokens();
  if (typeof window !== 'undefined') {
    window.location.replace(loginPageHref());
  }
}

async function syncPiniaAfterRefresh(data: LoginRes) {
  const { useAuthStore } = await import('../stores/auth');
  useAuthStore().applyRefreshedSession(data);
}

async function runTokenRefresh(): Promise<void> {
  const rt = getSysRefreshToken();
  if (!rt) {
    clearSysTokens();
    throw new Error('无刷新令牌');
  }

  let body: ResultLoginRes;
  try {
    body = await refreshAccessToken(rt);
  } catch {
    throw new Error('REFRESH_NETWORK');
  }
 
  if (body.code === SYS_CODE_RELOGIN) {
    forceReloginRedirect();
    throw new Error(body.msg || '请重新登录');
  }
 
  if (!isSysApiSuccess(body.code) || !body.data) {
    clearSysTokens();
    throw new Error(body.msg || '刷新令牌失败');
  }
  
  const { token, refreshToken, sysUserInfo } = body.data;
  setSysTokens(token, refreshToken);
  await syncPiniaAfterRefresh({ token, refreshToken, sysUserInfo });
}

function stripAuthHeader(config: InternalAxiosRequestConfig) {
  const h = config.headers;
  if (!h) return;
  if (typeof (h as { delete?: (key: string) => void }).delete === 'function') {
    (h as { delete: (key: string) => void }).delete('Authorization');
  } else {
    delete (h as Record<string, unknown>).Authorization;
  }
}

sysHttp.interceptors.request.use((config) => {
  const token = getSysToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
sysHttp.interceptors.response.use(
  async (response) => {
    if (response.status === 200 && isSysApiSuccess(response.data.code)) {
      return response.data;
    } else if (response.data.code === 2002) {
      try {
        if (!refreshChain) {
          refreshChain = runTokenRefresh().finally(() => {
            refreshChain = null;
          });
        }
        await refreshChain;
        response.config._retry = true;
        stripAuthHeader(response.config);
        return sysHttp.request(response.config);
      } catch {
        return Promise.reject(response.data);
      }
    } else {
      return Promise.reject(response.data);
    }
  },
  async (error) => {
    const status = error.response?.status as number | undefined;
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (status !== 401 || !originalRequest || originalRequest._retry) {
      return Promise.reject(error);
    }

    const path = String(originalRequest.url || '');
    if (path.includes('/auth/login') || path.includes('/auth/refresh-token')) {
      return Promise.reject(error);
    }

    if (!getSysRefreshToken()) {
      clearSysTokens();
      return Promise.reject(error);
    }


  }
);


